import { useEffect, useState } from "react";
import { ArrowDown, ArrowUp, ArrowUpDown, CalendarDays, ChevronLeft, ChevronRight, ChevronsLeft, ChevronsRight, SlidersHorizontal, X } from "lucide-react";
import { Button } from "../../ui/button";
import { Card } from "../../ui/card";
import { Input } from "../../ui/input";
import { ResponsivePopover, ResponsivePopoverContent, ResponsivePopoverTrigger } from "../../ui/responsive-popover";
import { Skeleton } from "../../ui/skeleton";
import { PAGE_SIZES, PERIOD_PRESETS, customError, formatDelta, nextOrdering, orderingDirection, pageWindow, presetLabel, rangeOf } from "../../../lib/jamoraAnalysis";
import { companyDay } from "../../../lib/time";
import { dayLabel } from "../../../lib/jamoraAdmin";

const selectClass = "h-9 rounded-md border border-slate-200 bg-white px-2.5 text-sm text-slate-800 focus:outline-none focus:ring-2 focus:ring-jamora/30";

// --- Période : raccourcis + dates libres, mémorisée dans ce navigateur (jamais côté serveur) ---
const STORE = "jamora.admin.period";
export function usePeriod() {
  const [value, setValue] = useState(() => {
    try { const saved = JSON.parse(localStorage.getItem(STORE) || "null"); if (saved && PERIOD_PRESETS.some(([id]) => id === saved.preset)) return saved; } catch { /* navigation privée */ }
    return { preset: "30d", from: "", to: "" };
  });
  const today = companyDay();
  const range = rangeOf(value.preset, today, value);
  const change = (next) => { setValue(next); try { localStorage.setItem(STORE, JSON.stringify(next)); } catch { /* ignoré */ } };
  return { value, range, change, label: value.preset === "custom" ? `${dayLabel(range.from)} → ${dayLabel(range.to)}` : presetLabel(value.preset) };
}

export function PeriodPicker({ period, testid = "period" }) {
  const [open, setOpen] = useState(false);
  const [custom, setCustom] = useState({ from: period.value.from, to: period.value.to });
  const error = customError(custom.from, custom.to);
  return (
    <ResponsivePopover open={open} onOpenChange={setOpen}>
      <ResponsivePopoverTrigger asChild>
        <Button variant="outline" size="sm" className="h-9 gap-2" data-testid={`${testid}-button`}><CalendarDays size={15} aria-hidden="true" />{period.label}</Button>
      </ResponsivePopoverTrigger>
      <ResponsivePopoverContent title="Période" align="start" className="w-72 space-y-1" data-testid={`${testid}-panel`}>
        {PERIOD_PRESETS.filter(([id]) => id !== "custom").map(([id, text]) => (
          <button key={id} type="button" onClick={() => { period.change({ preset: id, from: "", to: "" }); setOpen(false); }} data-testid={`${testid}-${id}`}
            className={`w-full text-left rounded-md px-3 py-2 text-sm ${period.value.preset === id ? "bg-jamora/10 text-jamora font-medium" : "hover:bg-slate-50"}`}>{text}</button>
        ))}
        <div className="border-t border-slate-100 pt-3 mt-2 space-y-2">
          <p className="text-xs font-medium text-slate-500">Dates précises</p>
          <div className="grid grid-cols-2 gap-2">
            <label className="text-xs text-slate-500">Du<Input type="date" value={custom.from} onChange={(e) => setCustom({ ...custom, from: e.target.value })} className="h-9 mt-1" data-testid={`${testid}-from`} /></label>
            <label className="text-xs text-slate-500">Au<Input type="date" value={custom.to} onChange={(e) => setCustom({ ...custom, to: e.target.value })} className="h-9 mt-1" data-testid={`${testid}-to`} /></label>
          </div>
          {error && (custom.from || custom.to) && <p className="text-xs text-red-700" role="alert" data-testid={`${testid}-error`}>{error}</p>}
          <Button size="sm" className="w-full" disabled={!!error} onClick={() => { period.change({ preset: "custom", ...custom }); setOpen(false); }} data-testid={`${testid}-apply`}>Appliquer</Button>
        </div>
      </ResponsivePopoverContent>
    </ResponsivePopover>
  );
}

// --- Bandeau d'indicateurs : chaque tuile peut filtrer la liste (onPick) ; l'évolution n'existe que si la période précédente a une valeur ---
export function KpiStrip({ items, loading, testid = "kpis" }) {
  if (loading) return <div className="grid grid-cols-2 lg:grid-cols-4 gap-3" data-testid={`${testid}-loading`}>{Array.from({ length: 6 }, (_, i) => <Skeleton key={i} className="h-20 rounded-lg" />)}</div>;
  return (
    <div className="grid grid-cols-2 lg:grid-cols-4 gap-3" data-testid={testid}>
      {items.map((k) => {
        const delta = k.previous !== undefined ? formatDelta(k.raw, k.previous) : null;
        const body = (
          <>
            <span className="block text-xs text-slate-500">{k.label}</span>
            <span className={`block text-lg font-semibold tabular-nums mt-0.5 ${k.tone === "alert" ? "text-red-700" : "text-slate-900"}`}>{k.value}</span>
            <span className="block text-xs mt-0.5 min-h-4">
              {delta ? <span className={delta.direction === "up" ? (k.invert ? "text-red-700" : "text-emerald-700") : delta.direction === "down" ? (k.invert ? "text-emerald-700" : "text-red-700") : "text-slate-500"} data-testid={`${testid}-${k.id}-delta`}>
                {delta.direction === "up" ? "▲ " : delta.direction === "down" ? "▼ " : ""}{delta.text} <span className="text-slate-400">vs période précédente</span></span>
                : k.hint ? <span className="text-slate-500">{k.hint}</span> : null}
            </span>
          </>
        );
        const cls = "rounded-lg border border-slate-200 bg-white px-3 py-2.5 text-left";
        return k.onPick
          ? <button key={k.id} type="button" onClick={k.onPick} aria-pressed={!!k.active} data-testid={`${testid}-${k.id}`} className={`${cls} hover:border-jamora/50 ${k.active ? "ring-2 ring-jamora/40" : ""}`}>{body}</button>
          : <div key={k.id} className={cls} data-testid={`${testid}-${k.id}`}>{body}</div>;
      })}
    </div>
  );
}

// --- Onglets d'état avec compteurs : [code, libellé, nombre|null] ---
export function CountTabs({ label, tabs, value, onChange, testid }) {
  return (
    <div className="flex gap-1 flex-wrap" role="tablist" aria-label={label}>
      {tabs.map(([code, text, count]) => (
        <button key={code || "all"} type="button" role="tab" aria-selected={value === code} onClick={() => onChange(code)} data-testid={`${testid}-${code || "all"}`}
          className={`rounded-full px-3 py-1.5 text-sm font-medium whitespace-nowrap ${value === code ? "bg-jamora text-white" : "bg-white text-slate-600 border border-slate-200 hover:bg-slate-50"}`}>
          {text}{count !== null && count !== undefined && <span className={`ml-1.5 text-xs tabular-nums ${value === code ? "text-white/80" : "text-slate-400"}`} data-testid={`${testid}-${code || "all"}-count`}>{count.toLocaleString("fr-FR")}</span>}
        </button>
      ))}
    </div>
  );
}

// --- Filtres : listes déroulantes visibles (`primary`) + « Plus de filtres » (le reste) + pastilles retirables ---
// filters: [{ param, label, options: [[value,label]], primary? }]  values: { param: value }
export function FilterBar({ filters, values, onChange, onClear, children, testid = "filters" }) {
  const primary = filters.filter((f) => f.primary);
  const more = filters.filter((f) => !f.primary);
  const active = filters.filter((f) => values[f.param]);
  const moreActive = more.filter((f) => values[f.param]).length;
  const select = (f, extra = "") => (
    <label key={f.param} className={`flex flex-col gap-1 text-xs text-slate-500 ${extra}`}>{f.label}
      <select className={selectClass} value={values[f.param] ?? ""} onChange={(e) => onChange(f.param, e.target.value)} data-testid={`${testid}-${f.param}`}>
        {f.options.map(([v, text]) => <option key={v || "all"} value={v}>{text}</option>)}
      </select>
    </label>
  );
  return (
    <div className="space-y-2" data-testid={testid}>
      <div className="flex items-end gap-2 flex-wrap">
        {children}
        {primary.map((f) => select(f, "hidden sm:flex"))}
        {(more.length > 0 || primary.length > 0) && (
          <ResponsivePopover>
            <ResponsivePopoverTrigger asChild>
              <Button variant="outline" size="sm" className="h-9 gap-2" data-testid={`${testid}-more`}><SlidersHorizontal size={15} aria-hidden="true" />Plus de filtres{(moreActive || 0) > 0 && <span className="rounded-full bg-jamora text-white text-xs px-1.5">{moreActive}</span>}</Button>
            </ResponsivePopoverTrigger>
            <ResponsivePopoverContent title="Filtres" align="start" className="w-80 space-y-3" data-testid={`${testid}-panel`}>
              {[...primary.map((f) => ({ f, mobileOnly: true })), ...more.map((f) => ({ f }))].map(({ f, mobileOnly }) => <div key={f.param} className={mobileOnly ? "sm:hidden" : ""}>{select(f)}</div>)}
            </ResponsivePopoverContent>
          </ResponsivePopover>
        )}
      </div>
      {active.length > 0 && (
        <div className="flex items-center gap-2 flex-wrap" data-testid={`${testid}-chips`}>
          {active.map((f) => (
            <button key={f.param} type="button" onClick={() => onChange(f.param, "")} aria-label={`Retirer le filtre ${f.label}`} data-testid={`${testid}-chip-${f.param}`}
              className="inline-flex items-center gap-1.5 rounded-full bg-jamora/10 text-jamora text-xs font-medium px-2.5 py-1 hover:bg-jamora/20">
              {f.label} : {(f.options.find(([v]) => v === values[f.param]) || [])[1] || values[f.param]}<X size={12} aria-hidden="true" />
            </button>
          ))}
          <button type="button" onClick={onClear} className="text-xs text-slate-500 underline" data-testid={`${testid}-clear`}>Tout effacer</button>
        </div>
      )}
    </div>
  );
}

// --- Pagination numérotée : « 51–100 sur 1 284 », début / précédent / suivant / fin, taille de page ---
export function PaginationBar({ list, onSize, size, noun, testid = "pager" }) {
  if (list.phase === "error" || (list.phase === "ready" && !list.total)) return null;
  const w = pageWindow(list.offset, list.total, size);
  const go = (offset) => list.setOffset(offset);
  const iconBtn = (label, icon, offset, disabled, id) => (
    <Button variant="outline" size="icon" className="h-9 w-9" disabled={disabled} onClick={() => go(offset)} aria-label={label} data-testid={`${testid}-${id}`}>{icon}</Button>
  );
  return (
    <div className="flex items-center justify-between gap-3 flex-wrap" data-testid={testid}>
      <p className="text-sm text-slate-600 tabular-nums" aria-live="polite" data-testid={`${testid}-range`}>
        {list.phase === "ready" ? `${w.from.toLocaleString("fr-FR")}–${w.to.toLocaleString("fr-FR")} sur ${list.total.toLocaleString("fr-FR")} ${noun}` : "…"}
      </p>
      <div className="flex items-center gap-2 flex-wrap">
        <label className="flex items-center gap-1.5 text-xs text-slate-500">Par page
          <select className={selectClass} value={size} onChange={(e) => onSize(Number(e.target.value))} data-testid={`${testid}-size`}>{PAGE_SIZES.map((n) => <option key={n} value={n}>{n}</option>)}</select>
        </label>
        <div className="flex items-center gap-1">
          {iconBtn("Première page", <ChevronsLeft size={16} />, 0, !w.hasPrev, "first")}
          {iconBtn("Page précédente", <ChevronLeft size={16} />, Math.max(0, list.offset - size), !w.hasPrev, "prev")}
          <span className="px-2 text-sm text-slate-600 tabular-nums" data-testid={`${testid}-page`}>Page {w.page} sur {w.pages}</span>
          {iconBtn("Page suivante", <ChevronRight size={16} />, list.offset + size, !w.hasNext, "next")}
          {iconBtn("Dernière page", <ChevronsRight size={16} />, w.lastOffset, !w.hasNext, "last")}
        </div>
      </div>
    </div>
  );
}

// --- En-tête de colonne triable (table) et sélecteur de tri (téléphone) ---
export function SortHeader({ column, ordering, onOrder }) {
  const dir = orderingDirection(ordering, column.sort);
  const Icon = dir === "ascending" ? ArrowUp : dir === "descending" ? ArrowDown : ArrowUpDown;
  return (
    <button type="button" onClick={() => onOrder(nextOrdering(ordering, column.sort))} data-testid={`sort-${column.sort}`}
      className={`inline-flex items-center gap-1 font-medium hover:text-slate-900 ${dir !== "none" ? "text-slate-900" : ""}`}>
      {column.label}<Icon size={13} className={dir === "none" ? "text-slate-300" : "text-jamora"} aria-hidden="true" />
    </button>
  );
}

export function MobileSort({ columns, ordering, onOrder, testid = "mobile-sort" }) {
  const sortable = columns.filter((c) => c.sort);
  if (!sortable.length) return null;
  const key = ordering.replace(/^-/, "");
  return (
    <div className="flex items-center gap-2 md:hidden">
      <label className="flex items-center gap-1.5 text-xs text-slate-500 flex-1">Trier par
        <select className={`${selectClass} flex-1`} value={key} onChange={(e) => onOrder(e.target.value ? (ordering.startsWith("-") ? `-${e.target.value}` : e.target.value) : "")} data-testid={testid}>
          <option value="">Par défaut</option>{sortable.map((c) => <option key={c.sort} value={c.sort}>{c.label}</option>)}
        </select>
      </label>
      <Button variant="outline" size="sm" className="h-9" disabled={!key} onClick={() => onOrder(ordering.startsWith("-") ? key : `-${key}`)} aria-label="Inverser le tri" data-testid={`${testid}-direction`}>
        {ordering.startsWith("-") ? <ArrowDown size={15} /> : <ArrowUp size={15} />}</Button>
    </div>
  );
}

export function BarList({ rows, testid }) {
  const max = Math.max(1, ...rows.map((r) => r.total));
  return (
    <Card className="p-4 space-y-2" data-testid={testid}>
      {rows.map((r) => (
        <div key={r.code} className="grid grid-cols-[7rem_minmax(0,1fr)_auto] items-center gap-3 text-sm">
          <span className="text-slate-700 truncate">{r.label}</span>
          <span className="h-2 rounded-full bg-slate-100 overflow-hidden"><span className="block h-full bg-jamora rounded-full" style={{ width: `${Math.max(2, (r.total / max) * 100)}%` }} /></span>
          <span className="tabular-nums text-slate-800 text-right">{r.display}</span>
        </div>
      ))}
    </Card>
  );
}

